import { useMentionCount } from '@/components/mentions/useMentionCount';
import { AtSign } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import {
	BookUser,
	ChevronRight,
	FolderPlus,
	Inbox,
	MailQuestion,
	Mail,
	Folders,
	MapPin,
	Tags,
	ContactRound,
	Building,
	MoreHorizontal,
	CalendarCheck,
	CalendarDays,
	Phone,
	ListFilter,
} from 'lucide-react';

import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@/reducers';
import { fetchAllReminders } from '@/actions/reminders';
import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';

import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
	SidebarGroup,
	SidebarGroupAction,
	SidebarGroupContent,
	SidebarGroupLabel,
	SidebarMenu,
	SidebarMenuAction,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubItem,
} from '@/components/ui/sidebar';

import { Link, useLocation } from 'react-router-dom';
import { NavLink } from 'react-router-dom';
import { CustomerConversation, Folder } from '@/types';
import {
	getUserDepartmentIds,
	isAssignedToUser,
	isUnassigned,
	isUnreadForUser,
} from '@/utils/webchat/conversationAssignment';
import { cn } from '@/lib/utils';
import CreateFolderDialog from '../webchat/chat/CreateFolderDialog';
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogClose,
} from '@/components/ui/dialog';
import { Button } from '../ui/button';
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { removeFolder, renameFolder } from '@/actions/folders';
import { deleteFolderFromAllCustomerConversations } from '@/actions/webchat';
import { Input } from '../ui/input';
import { useIsMobile } from '@/hooks/use-mobile';
import { ActionSheet } from '@/components/mobile';
import { useReminderCounts, useTodayEventBadge } from './use-nav-badges';
import { focusOpener } from './focus-return';

interface NavItem {
	_id?: string;
	name?: string;
	title?: string;
	[key: string]: any;
}

type FolderWithUrl = Folder & { url: string };

// Shape of state.auth.user that the counters depend on
interface AuthUser {
	_id: string;
	hasOwnIsReadIndicators?: boolean;
	[key: string]: any;
}

interface NavItemsParams {
	inboxOpenCount: number;
	isInboxUnread: boolean;
	unassignedOpenAndUnreadCount: {
		openConversations: number;
		unreadConversations: number;
	};
	myMessageOpenAndUnreadCount: {
		openConversations: number;
		unreadConversations: number;
	};
}

interface OrganizeItemsParams {
	folders?: FolderWithUrl[];
	departments: NavItem[] | null;
	locations?: NavItem[];
	users?: NavItem[];
	tags?: Array<{ title: string; url: string }>;
	handleCreateFolder: (value: boolean) => void;
	showCreateFolderDialog: boolean;
	isEnterpriseEnabled: boolean;
}

const getPrimaryViewItems = ({
	inboxOpenCount,
	isInboxUnread,
	unassignedOpenAndUnreadCount,
	myMessageOpenAndUnreadCount,
}: NavItemsParams) => [
	{
		title: 'Inbox',
		url: '/inbox',
		icon: Inbox,
		isActive: false,
		isUnread: isInboxUnread,
		rightDecorator: <p>{inboxOpenCount}</p>,
		shouldToggleOnly: false,
	},
	{
		title: 'Unassigned',
		url: '/unassigned',
		icon: MailQuestion,
		isActive: false,
		isUnread: unassignedOpenAndUnreadCount.unreadConversations > 0,
		rightDecorator: <p>{unassignedOpenAndUnreadCount.openConversations}</p>,
	},
	{
		title: 'My Messages',
		url: '/my-messages',
		icon: Mail,
		isActive: false,
		isUnread: myMessageOpenAndUnreadCount.unreadConversations > 0,
		rightDecorator: <p>{myMessageOpenAndUnreadCount.openConversations}</p>,
		shouldToggleOnly: false,
	},
];

const getOrganizeItems = ({
	folders,
	locations,
	departments,
	users,
	tags,
	handleCreateFolder,
	showCreateFolderDialog,
	isEnterpriseEnabled,
}: OrganizeItemsParams) => [
	{
		title: 'Folders',
		url: '/folders',
		icon: Folders,
		isActive: false,
		items: folders ?? [],
		shouldToggleOnly: true,
		actionButton: {
			action: handleCreateFolder,
			showCreateFolderDialog,
		},
	},
	isEnterpriseEnabled
		? {
				title: 'Locations',
				url: '/locations',
				icon: MapPin,
				isActive: false,
				items: locations,
				shouldToggleOnly: true,
			}
		: null,
	departments && departments.length > 0
		? {
				title: 'Departments',
				url: '/departments',
				icon: Building,
				isActive: false,
				items: departments,
				shouldToggleOnly: true,
			}
		: null,
	{
		title: 'Users',
		url: '/users',
		icon: ContactRound,
		isActive: false,
		items: users,
		shouldToggleOnly: true,
	},
	{
		title: 'Tags',
		url: '/tags',
		icon: Tags,
		isActive: false,
		items: tags,
		shouldToggleOnly: true,
	},
];

const inboxFilter = () => {
	return true;
};

const getUnassignedOpenAndUnreadCount = (
	customerConversations: CustomerConversation[],
	user: AuthUser,
) => {
	const unassignedConversations = customerConversations.filter(isUnassigned);

	return {
		openConversations: unassignedConversations.filter((c) => c.open).length,
		unreadConversations: unassignedConversations.filter((c) =>
			isUnreadForUser(c, user),
		).length,
	};
};

// "My Messages": assigned to the user directly, or to a department they belong to
const getUserOpenAndUnreadMessageCount = (
	customerConversations: CustomerConversation[],
	user: AuthUser,
	userDepartmentIds: string[],
) => {
	const myConversations = customerConversations.filter((c) =>
		isAssignedToUser(c, user._id, userDepartmentIds),
	);

	return {
		openConversations: myConversations.filter((c) => c.open).length,
		unreadConversations: myConversations.filter((c) =>
			isUnreadForUser(c, user),
		).length,
	};
};

const getOpenCount = (
	customerConversations: CustomerConversation[],
	filter: (convo: CustomerConversation) => boolean,
) => {
	return customerConversations.filter(filter).filter((c) => c.open).length;
};

const getUnreadConversations = (
	customerConversations: CustomerConversation[],
	user: AuthUser,
) => {
	return customerConversations.some((c) => isUnreadForUser(c, user));
};

const getItemsWithUrls = (items: NavItem[], baseUrl: string) => {
	return items.map((item) => ({
		...item,
		title: item.name || item.title,
		url: `${baseUrl}/${item._id}`,
	}));
};

const getTagsWithUrls = (tags: string[]) => {
	const tagItems = tags.map((tag) => ({
		title: tag,
		url: `/tags/${tag}`,
	}));

	// Add "Manage tags" link at the bottom
	tagItems.push({
		title: 'Manage tags',
		url: '/settings/tag-manager',
		isManageLink: true,
	} as any);

	return tagItems;
};

const getCompleteFolders = (
	folders: Folder[],
	baseUrl: string,
	businessGroups: NavItem[],
	customerConversations: CustomerConversation[],
	isEnterpriseEnabled: boolean,
) => {
	return folders.map((folder) => {
		const business = isEnterpriseEnabled
			? businessGroups.find((b) => b._id === folder._business)
			: null;

		const conversationCount = customerConversations.filter(
			(conversation) =>
				conversation._folder === folder._id && conversation.open,
		).length;

		return {
			...folder,
			title: folder.title,
			url: `${baseUrl}/${folder._id}`,
			hasActions: true,
			conversationCount,
			actions: [
				{ title: `${conversationCount} items`, action: null },
				{ title: 'Rename', actionType: 'rename' },
				{ title: 'Delete', actionType: 'delete' },
			],
			address: business?.address,
		};
	});
};

const getBusinessAndDepartments = (
	isEnterpriseEnabled: boolean,
	_temporaryBusiness: string,
	departments: NavItem[],
	businessGroups: NavItem[],
) => {
	if (isEnterpriseEnabled) {
		const departmentBusinesses = businessGroups
			.map((business) => {
				return {
					_id: business._id,
					heading: business.name,
					url: `/business/${business._id}/departments`,
					items: departments.filter(
						(department) =>
							department.isActive &&
							department._business === business._id,
					),
				};
			})
			.sort((a: NavItem, b: NavItem) =>
				a.heading && b.heading ? a.heading.localeCompare(b.heading) : 0,
			);
		return departmentBusinesses;
	}

	return departments.filter(
		(department: NavItem) =>
			department.isActive && department._business === _temporaryBusiness,
	);
};

const getBusinessAndUsers = (
	isEnterpriseEnabled: boolean,
	_temporaryBusiness: string,
	users: NavItem[],
	businessGroups: NavItem[],
) => {
	if (isEnterpriseEnabled) {
		const userBusinesses = businessGroups
			.map((business) => {
				return {
					_id: business._id,
					heading: business.name,
					url: `/business/${business._id}/users`,
					items: users
						.filter((user) =>
							user._businesses.includes(business._id),
						)
						.map((user) => {
							return {
								_id: user._id,
								title: user.name,
								url: `/users/${user._id}`,
							};
						}),
				};
			})
			.sort((a: NavItem, b: NavItem) =>
				a.heading && b.heading ? a.heading.localeCompare(b.heading) : 0,
			);
		return userBusinesses;
	}

	return users.filter((user) =>
		user._businesses.includes(_temporaryBusiness),
	);
};

export const NavWebchat = ({
	handleToggle,
	openStates,
	showFilterGlow = false,
}: {
	openStates: Record<string, boolean>;
	handleToggle: (title: string, isOpen: boolean) => void;
	showFilterGlow?: boolean;
}) => {
	const mentionCount = useMentionCount();
	const todayEventCount = useTodayEventBadge();
	const location = useLocation();
	const dispatch =
		useDispatch<ThunkDispatch<RootState, unknown, UnknownAction>>();

	const { pathname } = location;

	const [showCreateFolderDialog, setShowCreateFolderDialog] =
		useState<boolean>(false);

	const folders = useSelector((state: RootState) => state.folders) ?? [];
	const businessGroups =
		useSelector((state: RootState) => state.businessGroup) ?? [];
	const departments =
		useSelector((state: RootState) => state.departments) ?? [];
	const { customerConversations } = useSelector(
		(state: RootState) => state.webchat,
	);
	const users = useSelector((state: RootState) => state.users) ?? [];
	const tags = useSelector((state: RootState) => state.tags) ?? [];
	const { user, products } = useSelector((state: RootState) => state.auth);
	const hasVoiceProduct = products && products.includes('voice');
	const { overdue: overdueCount, dueToday: dueTodayCount } =
		useReminderCounts();

	useEffect(() => {
		const from = new Date();
		from.setMonth(from.getMonth() - 1);
		const to = new Date();
		to.setMonth(to.getMonth() + 1);
		dispatch(fetchAllReminders({ dateRange: { from, to } }));
	}, [dispatch]);

	// departments the current user belongs to; conversations assigned to
	// these count towards "My Messages" alongside direct assignments
	const userDepartmentIds = useMemo(
		() => getUserDepartmentIds(departments, user._id),
		[departments, user._id],
	);

	const isOrganizeActive =
		pathname.includes('/folders') ||
		pathname.includes('/locations') ||
		(pathname.includes('/departments') && !pathname.includes('settings')) ||
		pathname.includes('/users') ||
		pathname.includes('/tags');

	const activeUsersList = users.filter((u: any) => {
		if (!u.active) {
			return false;
		}
		if (user.isEnterpriseEnabled) {
			return u;
		}
		if (u._businesses.includes(user._temporaryBusiness) && u.active) {
			return u;
		}
		return false;
	});

	const departmentBusinesses = getBusinessAndDepartments(
		user.isEnterpriseEnabled,
		user._temporaryBusiness,
		getItemsWithUrls(departments, '/departments'),
		businessGroups,
	);

	const userBusinesses = getBusinessAndUsers(
		user.isEnterpriseEnabled,
		user._temporaryBusiness,
		getItemsWithUrls(activeUsersList, '/users'),
		businessGroups,
	);

	const primaryViews = getPrimaryViewItems({
		inboxOpenCount: getOpenCount(customerConversations, inboxFilter),
		isInboxUnread: getUnreadConversations(customerConversations, user),
		unassignedOpenAndUnreadCount: getUnassignedOpenAndUnreadCount(
			customerConversations,
			user,
		),
		myMessageOpenAndUnreadCount: getUserOpenAndUnreadMessageCount(
			customerConversations,
			user,
			userDepartmentIds,
		),
	});

	const organizeItems = getOrganizeItems({
		folders: getCompleteFolders(
			folders,
			'/folders',
			businessGroups,
			customerConversations,
			user.isEnterpriseEnabled,
		),
		locations: getItemsWithUrls(businessGroups, '/locations'),
		users: userBusinesses,
		tags: getTagsWithUrls(tags),
		departments: departments.length > 0 ? departmentBusinesses : [],
		handleCreateFolder: (value) => setShowCreateFolderDialog(value),
		showCreateFolderDialog,
		isEnterpriseEnabled: user.isEnterpriseEnabled,
	});

	return (
		<SidebarGroup>
			<SidebarGroupLabel>Conversations</SidebarGroupLabel>
			<SidebarGroupAction
				title="Toggle filters"
				aria-label="Toggle filters"
				aria-expanded={openStates['Filters'] ?? isOrganizeActive}
				onClick={() => {
					const isOpen = openStates['Filters'] ?? isOrganizeActive;
					handleToggle('Filters', !isOpen);
				}}
				className={cn(
					'transition-colors',
					(openStates['Filters'] ?? isOrganizeActive) &&
						'text-foreground',
					showFilterGlow &&
						'animate-pulse text-blue-500 ring-2 ring-blue-400/50 ring-offset-1 ring-offset-background rounded-md',
				)}
			>
				<ListFilter />
			</SidebarGroupAction>
			<SidebarGroupContent className="space-y-0.5">
				<SidebarMenu>
					{primaryViews.map((item) => (
						<Tree
							key={item.title}
							item={item}
							handleToggle={handleToggle}
							openStates={openStates}
						/>
					))}
          <SidebarMenuItem><SidebarMenuButton asChild isActive={pathname.startsWith('/mentions')}><Link to="/mentions"><AtSign /><span>Mentions</span>{mentionCount > 0 && <span className="ml-auto rounded-full bg-blue-600 px-2 text-xs text-white">{mentionCount > 99 ? '99+' : mentionCount}</span>}</Link></SidebarMenuButton></SidebarMenuItem>
				</SidebarMenu>

				{(openStates['Filters'] ?? isOrganizeActive) && (
					<SidebarMenu>
						{organizeItems.map(
							(item, index) =>
								item && (
									<Tree
										key={item.title || index}
										item={item}
										handleToggle={handleToggle}
										openStates={openStates}
									/>
								),
						)}
					</SidebarMenu>
				)}

				<div className="mx-3 my-2 border-t border-border/40" />

				<SidebarMenu>
					<SidebarMenuItem>
						<SidebarMenuButton
							asChild
							tooltip="Contacts"
							isActive={pathname.includes('/customers')}
						>
							<NavLink to="/customers" data-onboarding="contacts">
								<BookUser size={14} />
								<span>Contacts</span>
							</NavLink>
						</SidebarMenuButton>
					</SidebarMenuItem>
					<SidebarMenuItem>
						<SidebarMenuButton
							asChild
							tooltip="Reminders"
							isActive={pathname.includes('/reminders')}
						>
							<NavLink to="/reminders">
								<CalendarCheck size={14} />
								<div className="flex items-center justify-between w-full">
									<span>Reminders</span>
									{(overdueCount > 0 ||
										dueTodayCount > 0) && (
										<span
											className={`ml-2 text-xs font-semibold px-1.5 py-0.5 rounded-full ${
												overdueCount > 0
													? 'text-red-500 bg-red-100'
													: 'text-blue-500 bg-blue-100'
											}`}
										>
											{overdueCount + dueTodayCount}
										</span>
									)}
								</div>
							</NavLink>
						</SidebarMenuButton>
					</SidebarMenuItem>
					<SidebarMenuItem>
						<SidebarMenuButton
							asChild
							tooltip="Calendar"
							isActive={pathname.startsWith('/work/calendar')}
						>
							<NavLink to="/work/calendar">
								<CalendarDays size={14} />
								<div className="flex items-center justify-between w-full">
									<span>Calendar</span>
									{todayEventCount > 0 && (
										<span
											className="ml-2 text-xs font-semibold px-1.5 py-0.5 rounded-full text-blue-500 bg-blue-100"
											aria-label={`${todayEventCount} today`}
										>
											{todayEventCount}
											{/* VoiceOver reads the text, not a plain span's aria-label */}
											<span className="sr-only"> today</span>
										</span>
									)}
								</div>
							</NavLink>
						</SidebarMenuButton>
					</SidebarMenuItem>
					{hasVoiceProduct && (
						<SidebarMenuItem>
							<SidebarMenuButton
								asChild
								tooltip="Call History"
								isActive={pathname.includes('/call-history')}
							>
								<NavLink to="/call-history">
									<Phone size={14} />
									<span>Call History</span>
								</NavLink>
							</SidebarMenuButton>
						</SidebarMenuItem>
					)}
				</SidebarMenu>
			</SidebarGroupContent>
		</SidebarGroup>
	);
};

interface TreeItemProps extends NavItem {
	items?: TreeItemProps[];
	isActive?: boolean;
	isUnread?: boolean;
	rightDecorator?: React.ReactNode;
	shouldToggleOnly?: boolean;
	icon?: React.ElementType;
	heading?: string;
	hasActions?: boolean;
	isManageLink?: boolean;
	action?: (value?: any) => void;
	address?: string;
	actionButton?: {
		action: (value: boolean) => void;
		showCreateFolderDialog: boolean;
	};
}

const Tree = ({
	item,
	handleToggle,
	openStates,
}: {
	key?: React.Key;
	item: TreeItemProps;
	handleToggle: (title: string, isOpen: boolean) => void;
	openStates: Record<string, boolean>;
}) => {
	const location = useLocation();

	if (!item) {
		return null;
	}

	const isActive = item.url
		? isRouteActive(item.url, location.pathname)
		: false;

	const handleItemClick = (
		e: React.MouseEvent,
		currentItem: TreeItemProps,
	) => {
		if (currentItem.shouldToggleOnly) {
			e.preventDefault();
			handleToggle(
				currentItem.title || '',
				!openStates[currentItem.title || ''],
			);
		} else if (
			!openStates[currentItem.title || ''] &&
			currentItem.items?.length
		) {
			// Open the submenu if it's closed and has items
			handleToggle(currentItem.title || '', true);
		}
	};

	const renderNestedItems = () => (
		<SidebarMenuSub>
			{item.items &&
				item.items.map((subItem: TreeItemProps, index: number) => (
					<Tree
						key={index}
						item={subItem}
						handleToggle={handleToggle}
						openStates={openStates}
					/>
				))}
		</SidebarMenuSub>
	);

	const renderItemWithNoSubItems = () => {
		const button = (
			<SidebarMenuButton asChild isActive={isActive}>
				<Link
					to={item.url}
					data-onboarding={item.url === '/inbox' ? 'inbox' : undefined}
					className={cn(item.address && 'min-h-fit py-1.5')}
				>
					{item.icon && <item.icon size={14} />}

					<div className="flex items-center w-full min-w-0">
						<div className="flex items-center flex-1 min-w-0">
							<div
								className={cn(
									item.address && 'flex flex-col gap-0.5',
									'min-w-0',
								)}
							>
								{/* Block on phones so a long folder name ends in an
								    ellipsis before the row's "…" button. */}
								<span
									className={cn(
										item.isUnread ? 'font-bold' : '',
										'truncate max-md:block',
									)}
								>
									{item.title}
								</span>
								{item.address && (
									<span className="text-[11px] leading-tight text-muted-foreground truncate">
										{item.address}
									</span>
								)}
							</div>
							{item.isUnread && (
								<span className="flex h-2 w-2 shrink-0 rounded-full bg-primary ml-2" />
							)}
						</div>
						{item.rightDecorator && (
							<div className="ml-auto pl-2 shrink-0 text-xs">
								{item.rightDecorator}
							</div>
						)}
					</div>
				</Link>
			</SidebarMenuButton>
		);

		if (item.address) {
			return (
				<Tooltip>
					<TooltipTrigger asChild>{button}</TooltipTrigger>
					<TooltipContent side="right" className="max-w-60">
						<p className="font-medium">{item.title}</p>
						<p className="text-xs opacity-80">{item.address}</p>
					</TooltipContent>
				</Tooltip>
			);
		}

		return button;
	};

	if (item.heading && !item.title) {
		return (
			<SidebarMenuSubItem>
				<span className="text-xs text-muted-foreground">
					{item.heading}
				</span>
				{renderNestedItems()}
			</SidebarMenuSubItem>
		);
	}

	if (item.hasActions) {
		return (
			<SidebarMenuItem>
				{renderItemWithNoSubItems()}
				<SidebarItemWithActions item={item} />
			</SidebarMenuItem>
		);
	}

	if (item.isManageLink) {
		return (
			<SidebarMenuItem>
				<SidebarMenuButton
					asChild
					className="text-primary hover:text-primary hover:bg-primary/10"
				>
					<Link
						to={item.url}
						className="flex items-center gap-1 whitespace-nowrap"
					>
						<span className="font-medium">{item.title}</span>
						<ChevronRight className="size-3" />
					</Link>
				</SidebarMenuButton>
			</SidebarMenuItem>
		);
	}

	if ((!item.items || !item.items.length) && !item.shouldToggleOnly) {
		return <SidebarMenuItem>{renderItemWithNoSubItems()}</SidebarMenuItem>;
	}
	return (
		<SidebarMenuItem>
			<Collapsible
				className="group/collapsible"
				open={openStates[item.title]}
				onOpenChange={(isOpen) => {
					handleToggle(item.title, isOpen);
				}}
			>
				<SidebarMenuButton
					asChild
					isActive={isActive || item.isActive}
					tooltip={item.title}
				>
					<NavLink
						to={item.url}
						data-onboarding={item.url === '/inbox' ? 'inbox' : undefined}
						onClick={(event) => {
							handleItemClick(event, item);
						}}
					>
						{item.icon && (
							<item.icon
								className={cn(
									item.isUnread && 'fill-current',
									'size-4',
								)}
							/>
						)}

						<div className="flex flex-row items-center w-full">
							<div className="flex flex-row items-center flex-1 w-full">
								<span
									className={cn(
										'',
										item.isUnread ? 'font-bold' : '',
									)}
								>
									{item.title}
								</span>
								{item.actionButton && (
									<CreateFolderDialog
										open={
											item.actionButton
												.showCreateFolderDialog
										}
										setOpen={item.actionButton.action}
									>
										<button
											onClick={(e) => {
												e.preventDefault();
												e.stopPropagation();
												item.actionButton?.action(true);
											}}
											title="New Folder"
											aria-label="New folder"
											className="ml-1 opacity-0 group-hover/collapsible:opacity-100 transition-opacity text-muted-foreground hover:text-foreground focus-visible:opacity-100 touch:opacity-100 max-md:-my-2.5 max-md:ml-1 max-md:flex max-md:size-11 max-md:items-center max-md:justify-center max-md:opacity-100"
										>
											<FolderPlus className="size-3.5 max-md:size-5" />
										</button>
									</CreateFolderDialog>
								)}
								{item.isUnread && (
									<span className="flex h-2 w-2 rounded-full bg-primary ml-2" />
								)}
							</div>
							{item.rightDecorator && (
								<div className="ml-auto mr-1">
									{item.rightDecorator}
								</div>
							)}
						</div>
					</NavLink>
				</SidebarMenuButton>

				<CollapsibleTrigger asChild>
					<SidebarMenuAction
						className="data-[state=open]:rotate-90"
						aria-label={`Show ${item.title}`}
					>
						<ChevronRight />
					</SidebarMenuAction>
				</CollapsibleTrigger>
				<CollapsibleContent>{renderNestedItems()}</CollapsibleContent>
			</Collapsible>
		</SidebarMenuItem>
	);
};

type FolderDialogState = {
	folderId: string;
	title: string;
	type: 'rename' | 'delete';
	draft: string;
	open: boolean;
	// Phones open these dialogs from an action sheet rather than a trigger, so
	// Radix has nothing to hand focus back to; the folder's "…" button gets it.
	opener: HTMLElement | null;
} | null;
const FolderDialogContext = React.createContext<
	[FolderDialogState, React.Dispatch<React.SetStateAction<FolderDialogState>>] | null
>(null);

/**
 * Keeps a folder's Rename / Delete dialog, and the name being typed, above the
 * sidebar, which swaps between the phone drawer and the desktop sidebar (and
 * remounts every folder row) when a phone rotates across the md breakpoint.
 */
export function FolderDialogProvider({ children }: { children: React.ReactNode }) {
	const value = useState<FolderDialogState>(null);
	return (
		<FolderDialogContext.Provider value={value}>
			{children}
			<FolderDialog state={value[0]} setState={value[1]} />
		</FolderDialogContext.Provider>
	);
}

const FolderDialog = ({
	state,
	setState,
}: {
	state: FolderDialogState;
	setState: React.Dispatch<React.SetStateAction<FolderDialogState>>;
}) => {
	const dispatch =
		useDispatch<ThunkDispatch<RootState, unknown, UnknownAction>>();
	const isMobile = useIsMobile();
	// Keeps the content while it animates out.
	const close = () => setState((current) => current && { ...current, open: false });

	const deleteFolder = async () => {
		if (!state) return;
		dispatch(removeFolder({ _folder: state.folderId }));
		dispatch(
			deleteFolderFromAllCustomerConversations({
				_folder: state.folderId,
			}),
		);
		close();
	};

	const renameFolderLocal = async () => {
		if (!state) return;
		dispatch(
			renameFolder({
				newFolderTitle: state.draft,
				_folder: state.folderId,
			}),
		);
		close();
	};

	const returnFocus = (event: Event) => {
		if (isMobile && focusOpener(state?.opener)) event.preventDefault();
	};

	return (
		<Dialog open={!!state?.open} onOpenChange={(open) => !open && close()}>
			{state?.type === 'rename' && (
				// Phones: Cancel is in the footer, so the long title gets the
				// sheet's full width instead of running under a close button.
				<DialogContent hideClose="mobile" onCloseAutoFocus={returnFocus}>
					<DialogHeader>
						<DialogTitle>Rename {state.title}</DialogTitle>
					</DialogHeader>
					<Input
						value={state.draft}
						aria-label="Folder name"
						autoComplete="off"
						enterKeyHint="done"
						onChange={(e) => {
							const draft = e.target.value;
							setState((current) => current && { ...current, draft });
						}}
						// Phones: the keyboard's Done key renames, like a form.
						onKeyDown={(e) => {
							if (isMobile && e.key === 'Enter') {
								e.preventDefault();
								renameFolderLocal();
							}
						}}
					/>
					<DialogFooter>
						<DialogClose asChild>
							<Button variant="ghost">Cancel</Button>
						</DialogClose>
						<Button onClick={renameFolderLocal}>Rename</Button>
					</DialogFooter>
				</DialogContent>
			)}

			{state?.type === 'delete' && (
				<DialogContent hideClose="mobile" onCloseAutoFocus={returnFocus}>
					<DialogHeader>
						<DialogTitle>
							Are you sure you want to delete {state.title}?
						</DialogTitle>
					</DialogHeader>
					<DialogFooter>
						<DialogClose asChild>
							<Button variant="ghost">Cancel</Button>
						</DialogClose>
						<Button onClick={deleteFolder}>Delete</Button>
					</DialogFooter>
				</DialogContent>
			)}
		</Dialog>
	);
};

const SidebarItemWithActions = ({ item }: { item: TreeItemProps }) => {
	const isMobile = useIsMobile();
	const shared = React.useContext(FolderDialogContext);
	const local = useState<FolderDialogState>(null);
	const [folderDialog, setFolderDialog] = shared ?? local;
	const [showDropdown, setShowDropdown] = useState<boolean>(false);
	const optionsButton = React.useRef<HTMLButtonElement>(null);

	const openFolderAction = (actionType: string) => {
		if (!item._id || (actionType !== 'rename' && actionType !== 'delete')) return;
		setFolderDialog({
			folderId: item._id,
			title: item.title || '',
			type: actionType,
			draft: item.title || '',
			open: true,
			// Not document.activeElement: a tapped menu trigger doesn't take focus.
			opener: optionsButton.current,
		});
		setShowDropdown(false);
	};

	return (
		<>
			{/* Phones get an action sheet from the same "…" button. */}
			<DropdownMenu
				open={showDropdown && !isMobile}
				onOpenChange={setShowDropdown}
			>
				<DropdownMenuTrigger asChild className="relative">
					<SidebarMenuAction
						ref={optionsButton}
						className="absolute top-0 -right-5 size-4 max-md:right-1"
						aria-label={`${item.title} folder options`}
					>
						<MoreHorizontal />
					</SidebarMenuAction>
				</DropdownMenuTrigger>
				<DropdownMenuContent>
					{Array.isArray(item.actions) &&
						item.actions.map(
							(action: { title: string; actionType: string }) => (
								<DropdownMenuItem
									key={action.title}
									onClick={() => openFolderAction(action.actionType)}
								>
									{action.title}
								</DropdownMenuItem>
							),
						)}
				</DropdownMenuContent>
			</DropdownMenu>
			{isMobile && (
				<ActionSheet
					open={showDropdown}
					onOpenChange={setShowDropdown}
					title={item.title}
					description={`${item.conversationCount ?? 0} open ${item.conversationCount === 1 ? 'conversation' : 'conversations'}`}
					items={[
						{
							label: 'Rename',
							onSelect: () => openFolderAction('rename'),
							afterClose: true,
						},
						{
							label: 'Delete folder',
							destructive: true,
							onSelect: () => openFolderAction('delete'),
							afterClose: true,
						},
					]}
				/>
			)}
			{!shared && folderDialog?.folderId === item._id && (
				<FolderDialog state={folderDialog} setState={setFolderDialog} />
			)}
		</>
	);
};

export const isRouteActive = (itemUrl: string, pathname: string): boolean =>
	pathname === itemUrl || pathname.startsWith(`${itemUrl}/`);
